// config/region.js

// Detect region from current domain
export const detectRegion = () => {
  const hostname = window.location.hostname;

  // Vietnam — vn.honestlee.app or vn.honestlee.* (added 2026-04-25)
  if (hostname.includes('vn.honestlee.app') || hostname.includes('honestlee.vn')) {
    return 'vn';
  }
  if (hostname.includes('ae.honestlee.app') || hostname.includes('honestlee.ae')) {
    return 'ae';
  }
  // ✅ FIXED: Recognize both th.honestlee.app AND thai.honestlee.app
  if (hostname.includes('th.honestlee.app') || hostname.includes('thai.honestlee.app')) {
    return 'th';
  }
  if (hostname.includes('in.honestlee.app')) {
    return 'in';
  }
  // Malaysia — my.honestlee.app (added 2026-06-23)
  if (hostname.includes('my.honestlee.app') || hostname.includes('honestlee.my')) {
    return 'my';
  }
  if (hostname.includes('localhost') || hostname.includes('preview.emergentagent.com')) {
    // Local dev defaults to VN now (active market)
    return 'vn';
  }

  return 'global';
};

// Get API base URL
// ✅ PHASE 1: Unified API base resolver
// Returns base URL INCLUDING /api (convention: base has /api, paths don't)
export const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Priority 1: REACT_APP_BACKEND_URL (production)
  // If set, append /api to create full base
  if (process.env.REACT_APP_BACKEND_URL) {
    const backend = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, ''); // trim trailing slash
    return `${backend}/api`;
  }
  
  // Priority 2: REACT_APP_API_URL (legacy support)
  // If already includes /api, keep as-is; otherwise append /api
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  }
  
  // Priority 3: Local/preview - use relative /api (proxied by setupProxy.js)
  if (hostname.includes('localhost') || hostname.includes('preview.emergentagent.com') || hostname.includes('tail372ac1.ts.net')) {
    return '/api';
  }
  
  // Priority 4: If nothing set and not local, fail explicitly (no silent fallback)
  // This prevents accidentally hitting wrong backend in production
  console.error('❌ API base URL not configured! Set REACT_APP_BACKEND_URL in production.');
  console.error('   Falling back to relative /api (will only work if proxied)');
  return '/api';
};

// Get frontend URL based on region
export const getFrontendUrl = (region = null) => {
  const currentRegion = region || detectRegion();
  
  const regionUrls = {
    vn: 'https://vn.honestlee.app',
    ae: 'https://ae.honestlee.app',
    th: 'https://th.honestlee.app',
    in: 'https://in.honestlee.app',
    global: 'https://honestlee.app'
  };
  
  return regionUrls[currentRegion] || regionUrls.global;
};

// Region-specific configurations
const REGIONS = {
  vn: {
    // Backend region code (matches DB: honestlee_vn / Neon ai schema)
    region: 'vn',
    apiRegion: 'vn',

    // Display name
    displayName: 'Vietnam',
    displayNameLocal: 'Việt Nam',

    // Default map center — Đà Nẵng (active market 2026-04)
    mapCenter: {
      longitude: 108.2022,
      latitude: 16.0544,
      zoom: 13,
    },

    // Major cities — Đà Nẵng first because it's where venues live
    cities: [
      'Đà Nẵng', 'Hội An', 'Hà Nội', 'Hồ Chí Minh',
      'Nha Trang', 'Đà Lạt', 'Huế', 'Phú Quốc',
    ],
    defaultCity: 'Đà Nẵng',

    // Currency
    currency: 'VND',
    currencySymbol: '₫',

    // Phone
    phoneCode: '+84',
    phoneCodeNum: '84',
    phoneLength: 9,

    // Timezone
    timezone: 'Asia/Ho_Chi_Minh',

    // URLs
    apiUrl: null, // use getApiBaseUrl()
    frontendUrl: 'https://vn.honestlee.app',
  },

  ae: {
    // Backend region code (matches database: honestlee_ae)
    region: 'ae',
    apiRegion: 'ae', // ✅ FIXED: Backend expects 'ae' not 'uae'
    
    // Display name
    displayName: 'UAE/Dubai',
    displayNameLocal: 'الإمارات العربية المتحدة',
    
    // Default map center (Dubai, UAE)
    mapCenter: {
      longitude: 55.2708,
      latitude: 25.2048,
      zoom: 11
    },
    
    // Major cities
    cities: [
      'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman',
      'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'
    ],
    
    // Currency
    currency: 'AED',
    currencySymbol: 'د.إ',
    
    // Phone
    phoneCode: '+971',
    phoneCodeNum: '971',
    phoneLength: 9,
    
    // Timezone
    timezone: 'Asia/Dubai',
    
    // API URL
    apiUrl: null, // deprecated: use getApiBaseUrl()
    frontendUrl: 'https://ae.honestlee.app'
  },
  
  th: {
    // Backend region code (matches database: honestlee_th)
    region: 'th',
    apiRegion: 'th', // ✅ FIXED: Backend expects 'th' not 'thailand'
    
    // Display name
    displayName: 'Thailand',
    displayNameLocal: 'ประเทศไทย',
    
    // Default map center (Phuket, Thailand)
    mapCenter: {
    longitude: 98.3923,  // ✅ FIXED: Phuket coordinates
    latitude: 7.8804,
    zoom: 12
  },
    
    // Major cities
    cities: [
      'Bangkok', 'Phuket', 'Chiang Mai', 'Pattaya',
      'Krabi', 'Koh Samui', 'Hua Hin', 'Ayutthaya'
    ],
    
    // Currency
    currency: 'THB',
    currencySymbol: '฿',
    
    // Phone
    phoneCode: '+66',
    phoneCodeNum: '66',
    phoneLength: 9,
    
    // Timezone
    timezone: 'Asia/Bangkok',
    
    // API URL
    apiUrl: 'https://api.honestlee.ae',
    frontendUrl: 'https://th.honestlee.app'
  },
  
  in: {
    // Backend region code (matches database: honestlee_in)
    region: 'in',
    apiRegion: 'in', // ✅ FIXED: Backend expects 'in' not 'india'
    
    // Display name
    displayName: 'India',
    displayNameLocal: 'भारत',
    
    // Default map center (Mumbai, India)
    mapCenter: {
      longitude: 72.8777,
      latitude: 19.0760,
      zoom: 11
    },
    
    // Major cities
    cities: [
      'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad',
      'Chennai', 'Kolkata', 'Pune', 'Ahmedabad'
    ],
    
    // Currency
    currency: 'INR',
    currencySymbol: '₹',
    
    // Phone
    phoneCode: '+91',
    phoneCodeNum: '91',
    phoneLength: 10,
    
    // Timezone
    timezone: 'Asia/Kolkata',
    
    // API URL
    apiUrl: 'https://api.honestlee.ae',
    frontendUrl: 'https://in.honestlee.app'
  }
};

// Get current region config — fallback to vn (active market 2026-04)
export const getCurrentRegionConfig = () => {
  const currentRegion = detectRegion();
  return REGIONS[currentRegion] || REGIONS.vn;
};

// Export current region config as default
export const REGION_CONFIG = getCurrentRegionConfig();

// Export all regions for reference
export const ALL_REGIONS = REGIONS;